import { Search } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import lodash from "lodash";
import {
  Anchor,
  Breadcrumbs,
  Button,
  Divider,
  SimpleGrid,
  Space,
  TextInput,
} from "@mantine/core";
import { useClickOutside } from "@mantine/hooks";
import {
  Navigate,
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom";
import PageHeading from "../../../themes/components/PageHeading";
import PageContainer from "../../../themes/components/PageContainer";
import PayslipIssuesTable from "../components/PayslipIssuesTable";
import ProgressIndicator from "../../../themes/components/ProgressIndicator";
import ErrorPage from "../../../common/pages/ErrorPage";
import EmptyStateContainer from "../../../themes/components/EmptyStateContainer";
import DocumentImporterConfiguration from "../services/model/DocumentImporterConfiguration";
import { payslipJourneyConfiguration } from "../payslipJourneyConfiguration";
import useUnmatchedDocumentDetailsApiService from "../services/api/UnmatchedDocumentDetailsApiService";
import UnmatchedDocument from "../services/model/UnmatchedDocument";
import NotFound from "../../../common/pages/NotFound";
import { AccessPermissionContext } from "../../../providers/AccessPermissionsProvider";
import FeatureFlags from "../../../common/feature-flagging/FeatureFlags";
import { useEnabledFeatureFlag } from "../../../common/feature-flagging/FeatureFlagProvider";
import { AnalyticsContext } from "../../../providers/AnalyticsProvider";

const PayslipIssuesPage: React.FC = () => {
  const documentImporterConfiguration: DocumentImporterConfiguration =
    payslipJourneyConfiguration;

  const { hasPermissions } = React.useContext(AccessPermissionContext);
  const { trackDocumentsEvent } = React.useContext(AnalyticsContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const { state }: { state: any } = useLocation();
  const unmatchedPayslipsSearchBarEnabled = useEnabledFeatureFlag(
    FeatureFlags.UNMATCHED_PAYSLIPS_SEARCH_BAR
  );

  const previousSearchterm = state?.isSearchPresent
    ? state.previousSearchTerm
    : "";

  const [selection, setSelection] = useState<UnmatchedDocument[]>([]);
  const [searchTerm, setSearchTerm] = useState(previousSearchterm);
  const [unmatchedPayslipsrequest, setUnmatchedPayslipsrequest] = useState({
    id: id ? Number(id) : 0,
    search: searchTerm,
  });

  const { isLoading, isError, data, error } =
    useUnmatchedDocumentDetailsApiService({
      id: unmatchedPayslipsrequest.id,
      search: unmatchedPayslipsrequest.search,
    });

  const searchRef = useClickOutside(() => {
    if (searchTerm.trim().length === 0 && state) {
      state.isSearchPresent = false;
      setUnmatchedPayslipsrequest((prevState) => ({
        ...prevState,
        search: searchTerm,
      }));
    }
  });

  const debouncedSearchTermInput = useCallback(
    lodash.debounce((searchInputValue) => {
      if (searchInputValue.length === 0) {
        return;
      }
      trackDocumentsEvent(
        {
          event: `Unmatched_${documentImporterConfiguration.document_name_plural}_Dashboard_Search`,
          description: `User clicks and/or searches on the search bar on the unmatched ${documentImporterConfiguration.document_name_plural_lower_case} dashboard`,
          event_type: "Search_Bar_Input",
          type: documentImporterConfiguration.document_name_plural,
          job: data || null,
        },
        {
          search_input: searchInputValue || "",
        }
      );
    }, 500),
    [data, documentImporterConfiguration, trackDocumentsEvent]
  );

  useEffect(() => {
    if (data) {
      trackDocumentsEvent({
        event: `Unmatched_${documentImporterConfiguration.document_name_plural}_Dashboard`,
        description: `User views the unmatched ${documentImporterConfiguration.document_name_plural_lower_case} dashboard`,
        event_type: "PageView",
        type: documentImporterConfiguration.document_name_plural,
        job: data,
      });
    }
  }, [data, documentImporterConfiguration, trackDocumentsEvent]);

  useEffect(() => {
    const hasSearchTerm = searchTerm.trim().length > 0;
    if (hasSearchTerm) {
      debouncedSearchTermInput(searchTerm);
    }
  }, [searchTerm, debouncedSearchTermInput]);

  const searchUnmatchedPayslips = (keyCode: string): void => {
    if (keyCode === "Enter" && searchTerm.trim().length > 0) {
      setUnmatchedPayslipsrequest((prevState) => ({
        ...prevState,
        search: searchTerm,
      }));

      if (state) {
        state.isSearchPresent = true;
        state.previousSearchTerm = searchTerm;
      }
    }
  };

  let noResultsErrorMessage = "";
  if (data && data.documents.length === 0 && state?.isSearchPresent) {
    noResultsErrorMessage = `We couldn't find a match for "${searchTerm}"`;
  }

  if (!hasPermissions) {
    return <NotFound />;
  }

  if (!id) {
    return <Navigate to="/payslips" />;
  }

  if (isLoading) {
    return <ProgressIndicator />;
  }

  if (isError) {
    return <ErrorPage errors={error} />;
  }

  return (
    <PageContainer>
      <PageHeading
        title={`Unmatched ${documentImporterConfiguration.document_name_plural}`}
      />
      <Space
        style={{
          paddingTop: "32px",
        }}
      />
      <SimpleGrid cols={2} style={{ alignItems: "center" }}>
        <Breadcrumbs separator=">">
          {[
            {
              title: documentImporterConfiguration.document_name_plural,
              href: "/payslips",
            },
            { title: "Error", href: "#" },
            {
              title:
                !data || !data.documents.length
                  ? `document-job-${id}`
                  : data.batch_name,
              href: "",
            },
          ].map((item) => (
            <Anchor
              style={{
                color:
                  item.title === `document-job-${id}`
                    ? "#062133"
                    : "rgba(6, 33, 51, 0.5)",
              }}
              href={item.href}
              key={`payslips-path-${Math.random()}`}
            >
              {item.title}
            </Anchor>
          ))}
        </Breadcrumbs>

        {unmatchedPayslipsSearchBarEnabled && (
          <div>
            <TextInput
              ref={searchRef}
              error={noResultsErrorMessage}
              id="se"
              size="md"
              onKeyUp={(event) => searchUnmatchedPayslips(event.code)}
              onChange={(event) => {
                setSearchTerm(event.target.value);
              }}
              value={searchTerm}
              placeholder="Search"
              rightSection={
                <Button
                  color="jem-theme"
                  size="xs"
                  onClick={() => {
                    searchUnmatchedPayslips("Enter");
                  }}
                >
                  <Search size={18} />
                </Button>
              }
            />
            {noResultsErrorMessage.length === 0 && state?.isSearchPresent && (
              <span style={{ lineHeight: 1.5, marginTop: "5px" }}>
                {data?.documents.length} result
                {data && data?.documents.length > 1 ? "s" : ""} found
              </span>
            )}
          </div>
        )}
      </SimpleGrid>

      <Space h="xl" />
      <Divider my="sm" />

      {!data ||
        (!data.documents.length && !state?.isSearchPresent && (
          <EmptyStateContainer
            numberOfSpaces={1}
            title={`No unmatched ${documentImporterConfiguration.document_name_plural_lower_case}`}
            description={`All of the unmatched ${documentImporterConfiguration.document_name_lower_case} issues have been resolved.`}
            button={{
              onClick: () => navigate("/payslips"),
              title: `Back to ${documentImporterConfiguration.document_name_plural}`,
            }}
          />
        ))}

      {data && data.documents.length === 0 && state?.isSearchPresent && (
        <EmptyStateContainer
          numberOfSpaces={1}
          title={`No unmatched ${documentImporterConfiguration.document_name_plural_lower_case}`}
          description={`Your search returned no unmatched ${documentImporterConfiguration.document_name_lower_case}s.`}
          button={{
            onClick: () => window.location.reload(),
            title: `Clear Search`,
          }}
        />
      )}

      {data && data.documents.length > 0 && (
        <>
          <Space h="xl" />
          <PayslipIssuesTable
            documentImporterConfiguration={documentImporterConfiguration}
            data={data}
            selection={selection}
            onSelection={setSelection}
            jobStatus={state?.jobStatus}
          />
        </>
      )}
    </PageContainer>
  );
};

export default PayslipIssuesPage;
